// =====================================================================
// AIRCRAFT GALLERY — Gate 33 fleet reference
// =====================================================================
// Each aircraft has TWO upload slots:
//   - exterior photo  → [GATE33-D03-M11-AIRCRAFT-<KEY>-IMG-01]
//   - cabin layout    → [GATE33-D03-M11-CABIN-<KEY>-IMG-01]
// Upload images on /gate33-program/admin/images
// =====================================================================

import { useState, useEffect } from 'react';
import { CodedImage } from './components';
import { useProgram } from './ProgramContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const AIRCRAFT_REGISTRY = [
  {
    family: 'Bombardier',
    aircraft: [
      { name: 'Challenger 300 / 350', range: '4–5 h', pax: '8–9', galley: 'Microwave only — no oven, no fridge', crew: '1', key: 'CHALLENGER350' },
      { name: 'Challenger 650', range: '6–7 h', pax: '11', galley: 'Microwave + small fridge, no oven', crew: '1', key: 'CHALLENGER650' },
      { name: 'Global 5000 / 6000', range: '11–12 h', pax: '13', galley: 'Full — fridge, oven, microwave', crew: '1–2', key: 'GLOBAL6000' },
      { name: 'Global 7500 / 8000', range: '17 h', pax: '17', galley: 'Multi-zone — multiple ovens, wine storage', crew: '2', key: 'GLOBAL7500' },
    ],
  },
  {
    family: 'Gulfstream',
    aircraft: [
      { name: 'G450 / G550', range: '12–13 h', pax: '14–19', galley: 'Full — fridge, oven', crew: '1–2', key: 'G550' },
      { name: 'G650 / G650ER', range: '15–16 h', pax: '12–18', galley: 'Full — master suite, crew rest', crew: '2', key: 'G650' },
      { name: 'G700 / G800', range: '16–17 h', pax: '13–19', galley: 'Restaurant-grade, four living areas', crew: '2', key: 'G700' },
    ],
  },
  {
    family: 'Dassault',
    aircraft: [
      { name: 'Falcon 2000', range: '6 h', pax: '8–10', galley: 'Compact, well-designed', crew: '1', key: 'FALCON2000' },
      { name: 'Falcon 7X', range: '12 h', pax: '12–16', galley: 'Full meal service', crew: '1–2', key: 'FALCON7X' },
      { name: 'Falcon 8X', range: '13 h', pax: '12–16', galley: 'Full — upgraded from 7X', crew: '1–2', key: 'FALCON8X' },
      { name: 'Falcon 10X', range: '15 h+', pax: '16–19', galley: 'Widest cabin on market', crew: '2', key: 'FALCON10X' },
    ],
  },
  {
    family: 'Boeing & Airbus',
    aircraft: [
      { name: 'BBJ 737 / 777 / 787', range: '12–17 h', pax: '25–50', galley: 'Kitchen-grade, multiple ovens', crew: '2–4 + Head of Cabin', key: 'BBJ' },
      { name: 'ACJ A320 / A330 / A350', range: '12–18 h', pax: '25–50', galley: 'Kitchen-grade, walk-in fridge', crew: '2–4 + Head of Cabin', key: 'ACJ' },
    ],
  },
];

const aircraftCode = (key) => `[GATE33-D03-M11-AIRCRAFT-${key}-IMG-01]`;
const cabinCode = (key) => `[GATE33-D03-M11-CABIN-${key}-IMG-01]`;

// ---------- LIGHTBOX ----------
const LayoutLightbox = ({ url, name, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      data-testid="aircraft-layout-lightbox"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 14, 11, 0.92)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 32px',
        cursor: 'zoom-out',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        className="g33-eyebrow"
        style={{
          position: 'absolute',
          top: 28,
          left: 36,
          fontSize: 10,
          color: 'rgba(197, 168, 128, 0.85)',
          letterSpacing: '0.2em',
        }}
      >
        {name} · Cabin layout
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
        style={{
          position: 'absolute',
          top: 24,
          right: 28,
          background: 'transparent',
          border: '1px solid rgba(197, 168, 128, 0.5)',
          color: 'rgba(255, 252, 245, 0.9)',
          padding: '6px 14px',
          fontSize: 11,
          letterSpacing: '0.18em',
          cursor: 'pointer',
          textTransform: 'uppercase',
          fontFamily: 'inherit',
        }}
      >
        Close
      </button>
      <img
        src={url}
        alt={`${name} cabin layout`}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '92vw',
          maxHeight: '82vh',
          objectFit: 'contain',
          cursor: 'default',
          boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
        }}
      />
    </div>
  );
};

// ---------- AIRCRAFT CARD ----------
export const AircraftCard = ({ aircraft }) => {
  const { images } = useProgram();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const layoutEntry = images && images[cabinCode(aircraft.key)];
  const layoutUrl = layoutEntry?.url
    ? (layoutEntry.url.startsWith('/') ? `${API}${layoutEntry.url}` : layoutEntry.url)
    : null;

  return (
    <>
    <article
      data-testid={`aircraft-card-${aircraft.name.replace(/\s+/g, '-').toLowerCase()}`}
      style={{
        background: 'rgba(255, 252, 245, 0.5)',
        border: '1px solid rgba(201, 169, 97, 0.18)',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Exterior photo slot */}
      <div style={{ padding: 0 }}>
        <CodedImage
          code={aircraftCode(aircraft.key)}
          caption={aircraft.name}
          aspectRatio="16 / 10"
          fitMode="cover"
        />
      </div>
      <div style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
          <h4
            className="g33-display"
            style={{
              fontSize: 20,
              margin: 0,
              color: 'var(--g33-noir)',
              fontWeight: 400,
            }}
          >
            {aircraft.name}
          </h4>
          <div className="g33-eyebrow" style={{ fontSize: 10, color: 'var(--g33-or-deep)' }}>
            {aircraft.crew} crew
          </div>
        </div>

        {/* Cabin layout slot — click to open in lightbox */}
        <div style={{ padding: '4px 0', borderTop: '1px solid rgba(201, 169, 97, 0.12)', paddingTop: 16 }}>
          <div
            className="g33-eyebrow"
            style={{
              fontSize: 9,
              marginBottom: 8,
              color: 'var(--g33-or-deep)',
              opacity: 0.7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>Cabin layout</span>
            {layoutUrl && (
              <span style={{ opacity: 0.55, fontSize: 8, letterSpacing: '0.16em' }}>
                Click to expand
              </span>
            )}
          </div>
          <div
            onClick={() => layoutUrl && setLightboxOpen(true)}
            data-testid={`aircraft-layout-trigger-${aircraft.key.toLowerCase()}`}
            style={{
              cursor: layoutUrl ? 'zoom-in' : 'default',
              transition: 'opacity 0.3s ease',
            }}
            onMouseEnter={(e) => { if (layoutUrl) e.currentTarget.style.opacity = '0.82'; }}
            onMouseLeave={(e) => { if (layoutUrl) e.currentTarget.style.opacity = '1'; }}
          >
            <CodedImage
              code={cabinCode(aircraft.key)}
              aspectRatio="16 / 9"
              fitMode="contain"
            />
          </div>
        </div>

        {/* Specs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, lineHeight: 1.5, paddingTop: 8, borderTop: '1px solid rgba(201, 169, 97, 0.12)' }}>
          <div>
            <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 4 }}>Range</div>
            <div style={{ color: 'var(--g33-noir)' }}>{aircraft.range}</div>
          </div>
          <div>
            <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 4 }}>Pax</div>
            <div style={{ color: 'var(--g33-noir)' }}>{aircraft.pax}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--g33-charcoal)', paddingTop: 8, borderTop: '1px solid rgba(201, 169, 97, 0.12)' }}>
          <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 4 }}>Galley</div>
          <div>{aircraft.galley}</div>
        </div>
      </div>
    </article>
    {lightboxOpen && layoutUrl && (
      <LayoutLightbox
        url={layoutUrl}
        name={aircraft.name}
        onClose={() => setLightboxOpen(false)}
      />
    )}
    </>
  );
};

// ---------- FULL FLEET GALLERY ----------
export const AircraftFleetGallery = () => (
  <section data-testid="aircraft-fleet-gallery" style={{ padding: '40px 0 0' }}>
    {AIRCRAFT_REGISTRY.map((group) => (
      <div key={group.family} style={{ marginBottom: 72 }}>
        <h3
          className="g33-display"
          style={{
            fontSize: 'clamp(22px, 2.4vw, 30px)',
            marginBottom: 32,
            color: 'var(--g33-noir)',
          }}
        >
          {group.family}
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {group.aircraft.map((ac) => (
            <AircraftCard key={ac.name} aircraft={ac} />
          ))}
        </div>
      </div>
    ))}
  </section>
);
